// Typed fetch wrapper. Single place to add the Authorization header, parse
// JSON, and surface error responses as throws. Server returns
// `{ error: string }` for failures with a non-2xx status — we lift `error`
// onto the thrown Error.

import { getStoredJwt, clearStoredAuth } from './auth-storage';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  // Skip the auth header — used by the public auth endpoints.
  unauthenticated?: boolean;
  // Treat 401 as expected; don't auto-clear the stored JWT.
  allowUnauthorized?: boolean;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (!options.unauthenticated) {
    const jwt = getStoredJwt();
    if (jwt) headers['authorization'] = `Bearer ${jwt}`;
  }

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  // 204 No Content — return undefined cast to T so callers expecting void work.
  if (response.status === 204) {
    return undefined as T;
  }

  let parsed: unknown = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && !options.allowUnauthorized) {
      // Token rejected — wipe it so we re-prompt for sign-in. Keeps the rest
      // of the app from looping on a stale JWT after server-side revocation
      // or a 30d expiry.
      clearStoredAuth();
    }
    const message =
      parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as { error: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message, parsed);
  }

  return parsed as T;
}
